import { withPersistedQueries } from '@amazeelabs/silverback-graphql-persisted';
import { UnreachableCaseError } from '@luks/meta/src';
import {
  buildForm,
  buildLink,
  KitchenSinkRoute,
  RouteSlotInput,
} from '@luks/ui';
import { GraphQLClient } from 'graphql-request';
import { memoize } from 'lodash';
import * as Yup from 'yup';
import { create } from 'zustand';

import {
  getSdk,
  JobSearchSubscribeValidationError,
} from '../../../generated/mutations';
import queryMap from '../../../generated/persisted-queries-map.json';
import {
  ListingJobToolSearchFiltersInput,
  ListingJobToolSearchQuery,
  ListingJobToolSearchQueryVariables,
  LiveJobFragment,
  useInfiniteListingJobToolSearchQuery,
} from '../../../generated/queries';
import { formNavigate, useQueryParameters } from '../../commons/forms';
import { isDefined } from '../../commons/is-defined';
import { InferFormValues } from '../../commons/types';

type Slot = RouteSlotInput<typeof KitchenSinkRoute, 'body', 'JobListing'>;

type SlotInput = Exclude<Slot['input'], Function>;

type FormValues<TFormProp extends keyof SlotInput> = InferFormValues<
  SlotInput[TFormProp]
>;

type JobItem = SlotInput['items'][number];

type SubscriptionStore = {
  isSubmitted: boolean;
  filters: ListingJobToolSearchFiltersInput;
  setSubmitted: () => void;
  setFilters: (filters: ListingJobToolSearchFiltersInput) => void;
  reset: () => void;
};

const useSubscriptionStore = create<SubscriptionStore>((set) => ({
  isSubmitted: false,
  filters: {},
  setSubmitted: () => set({ isSubmitted: true }),
  setFilters: (filters) => set({ filters }),
  reset: () => set({ isSubmitted: false, filters: {} }),
}));

const errors = {
  required: 'Feld ist erforderlich',
  invalidEmail: 'Die E-mail-Addresse ist nicht gültig',
};

const sdk = getSdk(
  new GraphQLClient(process.env.GATSBY_GRAPHQL_ENDPOINT!, {
    fetch: withPersistedQueries(fetch, queryMap),
  }),
);

const aggregateResults = (pages?: Array<ListingJobToolSearchQuery>) =>
  pages?.reduce(
    (acc, value) => [
      ...acc,
      ...value.listingJobToolSearch.items.filter(isDefined).map(mapJob),
    ],
    [] as Array<JobItem>,
  ) || [];

const mapJob = memoize(
  (item: LiveJobFragment): JobItem => {
    return {
      title: item.title,
      location: item.location?.title,
      area: item.area?.title,
      workload: item.workload,
      id: item.id,
      Link: buildLink({ href: item.link?.url }),
    };
  },
  (item) => `job-${item.id}`,
);

const SearchForm = buildForm<FormValues<'SearchForm'>>({
  initialValues: {},
  onSubmit: (values) => {
    useSubscriptionStore.getState().reset();
    formNavigate()(values);
  },
  onReset: () => {
    useSubscriptionStore.getState().reset();
    formNavigate()({});
  },
  useInitialValues: useQueryParameters,
});

const SignupForm = buildForm<FormValues<'SignupForm'>>({
  initialValues: {
    subject: '',
    email: '',
  },
  onSubmit: async (values, formik) => {
    // Use Formik's status to indicate a general error.
    formik.setStatus(200);
    const setGeneralError = () => formik.setStatus(500);

    const { jobSearchSubscribe: result } = await sdk.JobSearchSubscribe({
      input: { ...values, ...useSubscriptionStore.getState().filters },
    });
    if (result.__typename === 'JobSearchSubscribeSuccess') {
      formik.setStatus(200);
      useSubscriptionStore.getState().setSubmitted();
    } else {
      switch (result?.__typename) {
        case undefined:
        case 'JobSearchSubscribeSubmissionError':
          setGeneralError();
          break;
        case 'JobSearchSubscribeValidationErrors':
          result.errors.forEach((error) => {
            switch (error) {
              case JobSearchSubscribeValidationError.InvalidEmail:
                formik.setErrors({ email: errors.invalidEmail });
                break;
              case JobSearchSubscribeValidationError.MissingSubject:
                formik.setErrors({ subject: errors.required });
                break;
              default:
                throw new UnreachableCaseError(error);
            }
          });
          break;
        default:
          throw new UnreachableCaseError(result);
      }
    }
  },
  validationSchema: Yup.object().shape({
    subject: Yup.string().required(errors.required),
    email: Yup.string().email(errors.invalidEmail).required(errors.required),
  }),
});

export function mapJobToolSearch(
  input:
    | ParagraphListingJobToolSearchFragment
    | ParagraphListingJobListFragment,
  context: { items: Array<any>; index: number },
): Slot {
  function isSearch(
    value:
      | ParagraphListingJobToolSearchFragment
      | ParagraphListingJobListFragment,
  ): value is ParagraphListingJobToolSearchFragment {
    return value.__typename === 'ParagraphListingJobToolSearch';
  }
  return {
    key: 'JobListing',
    input: function useListingJobToolSearch() {
      const pageSize = 20;

      const params = useQueryParameters<FormValues<'SearchForm'>>() || {};
      const filters: ListingJobToolSearchFiltersInput = isSearch(input)
        ? {
            text: params.keyword,
            location: params.locations,
            area: params.areas,
            workload: params.workloads,
          }
        : {
            text: input.keyword,
            location: input.location,
          };
      useSubscriptionStore((state) => state.setFilters)(filters);

      const variables: ListingJobToolSearchQueryVariables = {
        argument: (isSearch(input) && input.argument) || '',
        offset: 0,
        limit: pageSize,
        filters,
      };

      const {
        data,
        isLoading,
        isFetching,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
      } = useInfiniteListingJobToolSearchQuery(variables, {
        keepPreviousData: true,
        getNextPageParam: (lastPage, allPages) => {
          const displayedItems = allPages.reduce(
            (acc, val) => acc + (val.listingJobToolSearch.items.length || 0),
            0,
          );
          if (lastPage.listingJobToolSearch.total <= displayedItems) {
            return undefined;
          }
          return {
            ...variables,
            offset: displayedItems,
          };
        },
      });

      const latest = data?.pages.at(-1);

      const isSignupFormSubmitted = useSubscriptionStore(
        (state) => state.isSubmitted,
      );

      return [
        {
          title: input.title,
          totalItems: latest?.listingJobToolSearch.total || 0,
          items: aggregateResults(data?.pages),
          areas: (isSearch(input) && input.jobFilters.area.map(mapItem)) || [],
          locations:
            (isSearch(input) &&
              input.jobFilters.location?.filter(isDefined).map(mapItem)) ||
            [],
          workloads:
            (isSearch(input) && input.jobFilters.workload.map(mapItem)) || [],
          SearchForm,
          displaySearchForm: isSearch(input),
          SignupForm,
          isSignupFormSubmitted,
          infiniteScroll: context.index > context.items.length,
          MoreLink: hasNextPage
            ? buildLink({
                onClick: (ev) => {
                  ev.preventDefault();
                  fetchNextPage();
                },
              })
            : undefined,
        },
        isFetchingNextPage
          ? 102
          : isLoading || isFetching
          ? 101
          : data
          ? (latest?.listingJobToolSearch.total || 0) > 0
            ? 200
            : 404
          : 0,
      ];
    },
  };
}

const mapItem = (item: {
  id: string;
  title: string;
}): { id: string; label: string } => ({
  id: item.id,
  label: item.title,
});
